import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/providers/AuthProvider';
import { useFormErrors } from '@/hooks/useFormErrors';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { updateProfile } from '@/api/account';
import { authKeys, changePassword } from '@/api/auth';

/**
 * Profile — BUILD-PLAN §16 (name, email, phone, password), §31.
 *
 * Two forms, not one. Changing a password requires proving you know the
 * current one and signs other devices out; changing a display name does not.
 * Folding them together would either impose that friction on a trivial edit
 * or — far worse — let a password change ride along on a form that never
 * asked for the old one.
 */
export function ProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  usePageMetadata({ title: 'Profile' });

  return (
    <>
      <PageHeader
        eyebrow="My account"
        title="Profile"
        description="Your details, and how we reach you about an order."
        className="mb-8"
      />

      <div className="flex flex-col gap-12">
        {/*
          Keyed on the user id so the form remounts with fresh initial values
          if the session ever changes underneath it. Without the key the
          inputs would keep the previous account's details.
        */}
        <DetailsForm
          key={user?.id ?? 'none'}
          initial={{
            name: user?.name ?? '',
            email: user?.email ?? '',
            phone: user?.phone ?? '',
          }}
          emailVerified={user?.emailVerified ?? false}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: authKeys.user })}
        />

        <PasswordForm />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------ */

function FormHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="border-b border-line pb-3">
      <div className="flex items-center gap-3">
        <span className="h-px w-6 bg-accent/50" aria-hidden="true" />
        <h2 className="font-display text-xl text-ink">{title}</h2>
      </div>

      {hint && <p className="mt-2 max-w-md text-sm leading-relaxed text-muted">{hint}</p>}
    </div>
  );
}

function DetailsForm({
  initial,
  emailVerified,
  onSaved,
}: {
  initial: { name: string; email: string; phone: string };
  emailVerified: boolean;
  onSaved: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [notice, setNotice] = useState<string | null>(null);
  const { fieldErrors, formError, handleError, reset } = useFormErrors();

  const emailChanged = values.email.trim().toLowerCase() !== initial.email.toLowerCase();

  const mutation = useMutation({
    mutationFn: () =>
      updateProfile({
        name: values.name.trim(),
        email: values.email.trim(),
        /*
         | Empty means "no number", not an empty string. The column is
         | nullable, and sending "" stores a phone number that is blank —
         | which then reads as a number we hold but cannot call.
         */
        phone: values.phone.trim() === '' ? null : values.phone.trim(),
      }),
    onSuccess: () => {
      reset();
      setNotice(
        emailChanged
          ? 'Saved. Please check your new email address for a verification link.'
          : 'Your details have been saved.',
      );
      onSaved();
    },
    onError: (error) => {
      setNotice(null);
      handleError(error);
    },
  });

  return (
    <section>
      <FormHeading title="Your details" />

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
        className="mt-6"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <Input
            label="Full name"
            value={values.name}
            required
            autoComplete="name"
            error={fieldErrors['name']}
            onChange={(event) => setValues((v) => ({ ...v, name: event.target.value }))}
          />

          <Input
            label="Email address"
            type="email"
            value={values.email}
            required
            autoComplete="email"
            error={fieldErrors['email']}
            /*
             | The consequence is stated BEFORE the save, not after it.
             | Discovering that you have signed yourself out of your own
             | verified status is a bad way to learn what a field does.
             */
            hint={
              emailChanged
                ? 'You will need to verify this new address before we use it.'
                : emailVerified
                  ? 'Verified.'
                  : 'Not yet verified.'
            }
            onChange={(event) => setValues((v) => ({ ...v, email: event.target.value }))}
          />

          <Input
            label="Phone"
            type="tel"
            value={values.phone}
            autoComplete="tel"
            error={fieldErrors['phone']}
            hint="Optional. Used for delivery updates."
            onChange={(event) => setValues((v) => ({ ...v, phone: event.target.value }))}
          />
        </div>

        {notice && (
          <div className="mt-5">
            <Alert tone="success">{notice}</Alert>
          </div>
        )}

        {formError && (
          <div className="mt-5">
            <Alert tone="error">{formError}</Alert>
          </div>
        )}

        <div className="mt-6">
          <Button type="submit" loading={mutation.isPending}>
            Save details
          </Button>
        </div>
      </form>
    </section>
  );
}

function PasswordForm() {
  const empty = { current_password: '', password: '', password_confirmation: '' };

  const [values, setValues] = useState(empty);
  const [notice, setNotice] = useState<string | null>(null);
  const { fieldErrors, formError, handleError, reset } = useFormErrors();

  const mutation = useMutation({
    mutationFn: () => changePassword(values),
    onSuccess: () => {
      reset();
      // Cleared on success: leaving a password sitting in a form after it has
      // been used is a needless exposure on a shared machine.
      setValues(empty);
      setNotice('Your password has been updated. Other devices have been signed out.');
    },
    onError: (error) => {
      setNotice(null);
      handleError(error);
    },
  });

  return (
    <section>
      <FormHeading
        title="Password"
        hint="Changing this signs you out everywhere else, so a session someone else is holding cannot survive it."
      />

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
        className="mt-6"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <Input
            label="Current password"
            type="password"
            value={values.current_password}
            required
            autoComplete="current-password"
            error={fieldErrors['current_password']}
            onChange={(event) =>
              setValues((v) => ({ ...v, current_password: event.target.value }))
            }
          />

          {/* Holds the second column so the new-password pair sits on its own
              row rather than straddling the two groups. */}
          <span className="hidden sm:block" aria-hidden="true" />

          <Input
            label="New password"
            type="password"
            value={values.password}
            required
            autoComplete="new-password"
            error={fieldErrors['password']}
            onChange={(event) => setValues((v) => ({ ...v, password: event.target.value }))}
          />

          <Input
            label="Confirm new password"
            type="password"
            value={values.password_confirmation}
            required
            autoComplete="new-password"
            error={fieldErrors['password_confirmation']}
            onChange={(event) =>
              setValues((v) => ({ ...v, password_confirmation: event.target.value }))
            }
          />
        </div>

        {notice && (
          <div className="mt-5">
            <Alert tone="success">{notice}</Alert>
          </div>
        )}

        {formError && (
          <div className="mt-5">
            <Alert tone="error">{formError}</Alert>
          </div>
        )}

        <div className="mt-6">
          <Button type="submit" variant="secondary" loading={mutation.isPending}>
            Update password
          </Button>
        </div>
      </form>
    </section>
  );
}
